import { Box } from '@mui/material';
import { JSX, useState } from 'react';

import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { Checkbox } from 'Components/Checkbox/Checkbox';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE, ICON_SAVE } from 'Config/Icons';
import { TPRM_ASSESSMENT_CONFIGURATION_DELEGATES, TPRM_ASSESSMENT_CONFIGURATION_OWNER, UCRM_ASSESSMENT_CONFIGURATION_DELEGATES, UCRM_ASSESSMENT_CONFIGURATION_OWNER } from 'Config/Tooltips';
import { RequestState } from 'Helpers/RequestUtils';
import { getDueDiligenceModuleRoles } from 'Helpers/UserUtils';
import { CreateDueDiligenceAssessmentConfigurationRequest, DueDiligenceAssessmentConfiguration } from 'Models/DueDiligence';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { UserResponse } from 'Models/User';

import styles from './Modal.module.css';

export interface AddAssessmentModalProps {
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    existingNames: string[];
    hideModal: () => void;
    module: DueDiligenceModule;
    onSaved: (assessment: DueDiligenceAssessmentConfiguration) => void;
}

/** Allows the user to define the name, default owner, and default delegates of a new due diligence assessment, and whether it is a default assessment. */
export const AddAssessmentModal = (props: AddAssessmentModalProps): JSX.Element => {
    const [requestState, setRequestState] = useState<RequestState>({ type: 'idle' });

    const [name, setName] = useState('');
    const [isDefaultAssessment, setIsDefaultAssessment] = useState(true);
    const [selectedOwner, setSelectedOwner] = useState<UserResponse>();
    const [selectedDelegates, setSelectedDelegates] = useState<UserResponse[]>([]);

    const handleSubmit = async () => {
        const trimmedName = name.trim();

        if (trimmedName.length === 0) {
            setRequestState({ type: 'failure', message: 'Name is required.' });
            return;
        }

        if (props.existingNames.includes(trimmedName)) {
            setRequestState({ type: 'failure', message: 'An assessment with this name already exists.' });
            return;
        }

        if (!selectedOwner) {
            setRequestState({ type: 'failure', message: 'Default owner is required.' });
            return;
        }

        const request: CreateDueDiligenceAssessmentConfigurationRequest = {
            default_delegates_ids: selectedDelegates.map((delegate) => delegate.cognito_subject),
            default_owner_id: selectedOwner.cognito_subject,
            is_default: isDefaultAssessment,
            name: trimmedName,
        };

        setRequestState({ type: 'inProgress' });
        try {
            const response = await props.dueDiligenceAssessmentConfigurationApi.createAssessmentConfiguration(request);
            setRequestState({ type: 'success', message: 'Assessment saved.' });
            props.onSaved(response.data);
        } catch (error) {
            setRequestState({ type: 'failure', message: error.message });
        }
    };

    return (
        <Modal open header="Add Assessment">
            {requestState.type === 'failure' && <Alert type="error">{requestState.message}</Alert>}
            {requestState.type === 'success' && <Alert type="success">{requestState.message}</Alert>}

            <Box component="form" noValidate>
                <div className={styles.formField}>
                    <FormFieldText formFieldId="name" formFieldLabel="Name" value={name} handleChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)} required />
                </div>

                <div className={styles.checkboxRow}>
                    <Checkbox label="Default Assessment" checked={isDefaultAssessment} onToggled={setIsDefaultAssessment} />
                </div>

                <div className={styles.formField}>
                    <FormFieldUserSelect rolesRestriction={getDueDiligenceModuleRoles(props.module)} tooltip={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_CONFIGURATION_OWNER : TPRM_ASSESSMENT_CONFIGURATION_OWNER} formFieldId="defaultOwnerId" formFieldLabel="Default Owner" selectedUser={selectedOwner} onUserSelected={(user) => setSelectedOwner(user)} required />
                </div>

                <div className={styles.formField}>
                    <FormFieldUserMultiSelect rolesRestriction={getDueDiligenceModuleRoles(props.module)} tooltip={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_CONFIGURATION_DELEGATES : TPRM_ASSESSMENT_CONFIGURATION_DELEGATES} formFieldId="defaultDelegates" formFieldLabel="Default Delegates" selectedUsers={selectedDelegates} onUsersSelected={(users) => setSelectedDelegates(users ?? [])} />
                </div>

                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE} disabled={requestState.type === 'inProgress'}>
                        {requestState?.type === 'success' ? 'Close' : 'Cancel'}
                    </Button>
                    <Button onClick={handleSubmit} variant="primary" isLoading={requestState.type === 'inProgress'} loadingText="Saving..." disabled={requestState?.type === 'inProgress' || requestState?.type === 'success'} fontAwesomeImage={ICON_SAVE}>
                        Save
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
